<template>
	<button
		ref="button"
		class="portal-icon-button"
		:class="`portal-icon-button--${tone}`"
		type="button"
		:aria-label="label"
		@click="emit('click')"
	>
		<slot />
	</button>
</template>

<script setup lang="ts">
import { ref } from 'vue'

/**
 * The 40px icon tile the shell's chrome uses (Figma "Button base" 14254:175759 in the header, the close in the
 * side menu): `dark` sits on the brand-green bar with the accent glyph, `light` is the white tile on the menu.
 * The DS `ButtonIcon` (plain / 28px square / circle) has no 40px radius-16 tile, so this is app chrome — ADR 0021.
 */

defineProps<{
	/** The accessible name; these buttons have no visible text. */
	label: string
	tone: 'dark' | 'light'
}>()

const emit = defineEmits<{ click: [] }>()

const button = ref<HTMLButtonElement | null>(null)

defineExpose({
	/** Focus without scrolling: the button sits in the sticky header, and a page scrolled far down must stay where it is. */
	focus: () => button.value?.focus({ preventScroll: true }),
})
</script>

<style scoped>
.portal-icon-button {
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: var(--size-40);
	height: var(--size-40);
	padding: 0;
	border: 0;
	border-radius: var(--size-16);
	cursor: pointer;
}

.portal-icon-button:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

.portal-icon-button--dark {
	background: transparent;
	color: var(--button-active-accent);
}

.portal-icon-button--dark:hover {
	background: var(--button-hover-overlay-on-dark);
}

.portal-icon-button--dark:focus-visible {
	outline-color: var(--button-active-accent);
}

.portal-icon-button--light {
	background: var(--background-white);
	color: var(--text-dark-primary);
}
</style>
